import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Datetimebox } from '../../controls/datetimebox';
import { Textbox } from '../../controls/textbox';
import { Layouts } from '../../controls/layouts';
import { HeaderComponent } from '../..//controls/components/basic/header/header.component';
import { DateTimeService2 } from 'src/app/shared/datetime/datetime.service2';
import { Password, type PasswordComponentValue } from '../../controls/password';
import { ToggleButton, ToggleButtonItem } from '../../controls/togglebutton';
import { RadioButtonGroup } from '../../controls/radio-button-group';
import { Checkbox } from '../../controls/checkbox';

@Component({
  selector: 'app-control-test-decorators',
  standalone: true,
  templateUrl: './control-test-decorators.component.html',
  styleUrl: './control-test-decorators.component.scss',
  imports: [FormsModule, Layouts, Textbox, Password, RadioButtonGroup, Checkbox, Datetimebox, ToggleButton, HeaderComponent],
})
export class ControlTestDecoratorsComponent {
  readonly dateTimeService = inject(DateTimeService2);

  name: string = 'John Doe';
  address: string = '123 Main St';
  acceptTerms: boolean = false;
  passwordHash: string | null = null;
  passwordSecret = { id: '001c1d1fa3624f30b4491b4315cfa383', value: null } as PasswordComponentValue;
  possibleSizes = [
    { value: 'S', description: 'Small' },
    { value: 'M', description: 'Medium' },
    { value: 'L', description: 'Large' }
  ];
  selectedSize = 'M';

  onSizeChange(newSize: unknown) {
    this.selectedSize = newSize as string;
  }

  colors: ToggleButtonItem[] = [
    {
      key: 'blue',
      text: 'Blue',
      icon: '',
      hint: 'Use blue color'
    },
    {
      key: 'red',
      text: 'Red',
      icon: '',
      hint: 'Use red color'
    }
  ];
  selectedColors = ['blue'];

  onColorChange(newToggle: unknown) {
    this.selectedColors = newToggle as string[];
  }

  readonly datetime = signal(new Date(2025, 9, 16, 15, 8, 12));
  readonly date = signal(new Date(2025, 9, 16, 0, 0, 0));
  readonly time = signal(new Date(2025, 9, 16, 11, 37, 42));
  readonly minDateTime = signal(new Date(2025, 9, 10, 15, 8, 12));
  readonly maxDateTime = signal(new Date(2025, 9, 20, 15, 8, 12));

  readonly dateTimeFormat = computed(() => this.dateTimeService.getDateTimeFormat()); // "yyyy-MM-dd HH:mm:ss"
  readonly dateFormat = computed(() => this.dateTimeService.getDateFormat());         // "yyyy-MM-dd"
  readonly timeFormat = computed(() => this.dateTimeService.getTimeFormat());         // "HH:mm"

  readonly formattedDateTime = computed(() => this.dateTimeService.getFormattedDateTime(this.datetime())); // "2025-10-16 15:08:12"
  readonly formattedDate = computed(() => this.dateTimeService.getFormattedDate(this.date()));             // "2025-10-16"
  readonly formattedTime = computed(() => this.dateTimeService.getFormattedTime(this.time()));             // "11:37"

  // --- String input
  readonly stringInput = '2024-05-02T14:30:00';
  readonly formattedFromString = computed (() => this.dateTimeService.getFormattedDateTime(this.stringInput)); // "2024-05-02 14:30:00"

  // --- Unix timestamp input
  readonly timestampInput = 1729087200000; // 2024-10-16 18:00:00 UTC
  readonly formattedFromTimestamp = computed(() => this.dateTimeService.getFormattedDateTime(this.timestampInput));

  // --- midnight ---
  readonly midnight = signal(new Date(2025, 0, 1, 0, 0, 0));
  readonly formattedMidnight = computed(() => this.dateTimeService.getFormattedDateTime(this.midnight())); // "2025-01-01 00:00:00"

  // --- endOfDay  ---
  readonly endOfDay = signal(new Date(2025, 11, 31, 23, 59, 59));
  readonly formattedEndOfDay = computed(() => this.dateTimeService.getFormattedDateTime(this.endOfDay())); // "2025-12-31 23:59:59"

  // --- leapDay ---
  readonly leapDay = signal(new Date(2024, 1, 29, 12, 0, 0));
  readonly formattedLeapDay = computed(() => this.dateTimeService.getFormattedDate(this.leapDay())); // "2024-02-29"
}
